// 📄 src/pages/TacomaPartsShowcase.jsx
import React, { useEffect, useState } from "react";
import { shopifyHelpers } from "../utils/shopify";
import { TOP16_HANDLES, TOP16_TITLE_MATCHES } from "../utils/top16";
import { CATEGORY_TAGS, GENERATION_TAGS } from "../utils/tagMappings";
import ProductCard from "../components/ProductCard";
import bgtrace from "../assets/bgtrace1.jpeg";

const TacomaPartsShowcase = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [top16, setTop16] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All Categories");
  const [generation, setGeneration] = useState("All Gens");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadProducts() {
      try {
        const products = await shopifyHelpers.getAllProducts(250);

        let topProducts = products.filter(
          (p) =>
            TOP16_HANDLES.includes(p.handle) ||
            TOP16_TITLE_MATCHES.some((match) =>
              p.title.toLowerCase().includes(match.toLowerCase())
            )
        );

        const giftItems = topProducts.filter(
          (p) =>
            p.handle === "gift-card" ||
            p.handle === "the-taco-garage-gift-certificate"
        );
        const nonGift = topProducts.filter(
          (p) =>
            p.handle !== "gift-card" &&
            p.handle !== "the-taco-garage-gift-certificate"
        );
        topProducts = [...nonGift, ...giftItems];

        setTop16(topProducts);
        setAllProducts(products);
      } catch (err) {
        console.error("❌ Error loading products:", err);
      } finally {
        setLoading(false);
      }
    }
    loadProducts();
  }, []);

  const filteredProducts = allProducts.filter((p) => {
    const matchesSearch = p.title.toLowerCase().includes(search.toLowerCase());
    const matchesCategory =
      category === "All Categories" ||
      (category === "Top Sellers" &&
        (TOP16_HANDLES.includes(p.handle) ||
          TOP16_TITLE_MATCHES.some((match) =>
            p.title.toLowerCase().includes(match.toLowerCase())
          ))) ||
      (CATEGORY_TAGS[category] &&
        p.tags.some(
          (tag) => tag.toLowerCase() === CATEGORY_TAGS[category].toLowerCase()
        ));
    const matchesGeneration =
      generation === "All Gens" ||
      (GENERATION_TAGS[generation] &&
        p.tags.some(
          (tag) =>
            tag.toLowerCase() === GENERATION_TAGS[generation].toLowerCase()
        ));
    return matchesSearch && matchesCategory && matchesGeneration;
  });

  const filtersActive =
    search || category !== "All Categories" || generation !== "All Gens";

  if (loading)
    return <div className="p-10 text-black">Loading products...</div>;

  return (
    // ✅ Mobile: topo background with overlay, Desktop: transparent
    <section 
      className="relative py-20 border-t border-gray-200 transition-all duration-300 md:bg-none"
      style={{
        backgroundImage: `url(${bgtrace})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* 🌎 Mobile-only white overlay for readability */}
      <div className="absolute inset-0 bg-white/80 md:bg-transparent pointer-events-none"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto p-4 sm:p-6">
        <h1 className="text-5xl font-extrabold mb-8 pt-10 tracking-tight text-center">
          <span className="text-black">Tacoma</span>{" "}
          <span className="text-brandred">Performance Parts</span>
        </h1>

        {/* 🔎 Search + Filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 justify-center mb-8">
          <input
            type="text"
            placeholder="Search for parts, accessories, or installations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded px-3 py-2 w-full sm:w-1/2 md:w-1/3"
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="border rounded px-3 py-2 w-full sm:w-40"
          >
            <option>All Categories</option>
            <option>Top Sellers</option>
            {Object.keys(CATEGORY_TAGS).map((cat) => (
              <option key={cat}>{cat}</option>
            ))}
          </select>
          <select
            value={generation}
            onChange={(e) => setGeneration(e.target.value)}
            className="border rounded px-3 py-2 w-full sm:w-40"
          >
            <option>All Gens</option>
            {Object.keys(GENERATION_TAGS).map((gen) => (
              <option key={gen}>{gen}</option>
            ))}
          </select>
        </div>

        {/* 🧩 Product Grid — dark container only */}
        <div className="bg-gray-900/95 text-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-800">
          {!filtersActive && (
            <section>
              <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {top16.slice(0, 16).map((product, index) => (
                  <div 
                    key={product.id}
                    className={index >= 5 ? "hidden md:block" : ""}
                  >
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {filtersActive && (
            <section>
              <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {filteredProducts.length > 0 ? (
                  filteredProducts.slice(0, 16).map((product, index) => (
                    <div 
                      key={product.id}
                      className={index >= 5 ? "hidden md:block" : ""}
                    >
                      <ProductCard product={product} />
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-400 col-span-full">
                    🚫 No products match your filters.
                  </p>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </section>
  );
};

export default TacomaPartsShowcase;
